import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { useQuery } from "react-query";
import classnames from "classnames";
import { toast } from "extensions/toast";
import isPlainObject from "lodash-es/isPlainObject";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faTimes } from "@fortawesome/pro-light-svg-icons";

import { toHex } from "common/colors";
import { copyToClipboard } from "common/utils";

import { fetchRequests } from "common/actions/requests";
import { TimelineChart } from "common/components/charts/react/timeline";
import { parse } from "common/components/datePicker/dateMath";
import { Placeholder } from "common/components/placeholder";

import { Tooltip } from "common/components/tooltip";

export function DetailsBoxRedux({
    details,
    closeDetails
}) {
    const qualifiers = useSelector((state) => state.requests.qualifiers);
    const q = new Qualifiers({ value: qualifiers });
    q.addOrReplace(`uri_template:${Qualifiers.normalizeValue(details.uri_template)}`);

    const { data, status } = useQuery(
        [
            "requests",
            {
                qualifiers: q.toString(),
                pivots: ["date_time"],
                date_range: {
                    startDate: parse("now-M", "start").toISOString(),
                    endDate: parse("now", "end").toISOString()
                }
            }
        ],
        fetchRequests
    );
    
    const normalizedDetailsValue = (value) => {
        if (isPlainObject(value) || Array.isArray(value)) return JSON.stringify(value);
        if (value === null) return "Not available";
        if (value === true) return "True";
        if (value === false) return "False";
        return value;
    };
    
    const copyTextToClipboard = (value) => (e) => {
        e.preventDefault();
        copyToClipboard(value);
        toast.success("Copied to clipboard");
    };
    
    return (
        <div className="details-box">
            <header>
                <div className="title">Request details
                    <Tooltip title="Copy to clipboard">
                        <span onClick={copyTextToClipboard(JSON.stringify(details, null, 4))}><FontAwesomeIcon className="copy-icon" icon={faCopy} /></span>
                    </Tooltip>
                </div>
                <div className="bar">
                    <Tooltip title="Close details">
                        <span onClick={closeDetails}><FontAwesomeIcon className="close-trans" icon={faTimes}/></span>
                    </Tooltip>
                </div>
            </header>
            
            <div className="request-trend">
                {status === "loading"
                    ? <div style={{ padding: 16 }}><Placeholder size="long" /></div>
                    : (
                        <TimelineChart
                            freq="day"
                            height={150}
                            series={[
                                {
                                    type: "line",
                                    name: "Last month performance",
                                    pointFormat: ["date_time", (point) => point.average_request_time * 1000],
                                    color: toHex("green-leaf"),
                                    seriaPointer: "last_month",
                                    valueType: "number",
                                    zIndex: 2
                                }
                            ]}
                            data={{ last_month: data }}
                            yAxisProps={{
                                softMax: details.elapsed_time * 1000,
                                softMin: details.elapsed_time * 1000
                            }}
                            yAxisTitle="Average request duration (in ms)"
                            yAxisLabelFormat="{value:,.0f}"
                            tooltipPointFormat="<b>{point.y:,.0f} ms</b>"
                            yAxisPlotLines={[{
                                value: details.elapsed_time * 1000,
                                color: toHex("blue-light"),
                                dashStyle: "shortdash",
                                width: 2,
                                zIndex: 3,
                                label: {
                                    text: "This request duration"
                                }
                            }]}
                        />
                    )}
            </div>
            
            <div className="txn-wrapper">
                {Object.entries(details).map(([key, value]) => (
                    <div className="pair" key={key}>
                        <div className="key">{key}</div>
                        <div
                            className={classnames("value", { na: value === null })}
                        >{normalizedDetailsValue(value)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
